import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl } from '@/api/backendAuth';
import { useAuth } from '@/modules/Auth/hooks/useAuth';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface TenantQuotaUsageSummary {
  billingPeriodKey: string;
  packageName: string | null;
  /** Present when the tenant has a PackageId (matches checkout plan `id` from GET /billing/paddle/plans). */
  packageId: string | null;
  eventsUsedThisBillingPeriod: number;
  maxEventsPerBillingPeriod: number | null;
  eventsRemaining: number | null;
  usersTracked: number;
  maxUsers: number | null;
  usersRemaining: number | null;
  workspacesUsed: number;
  maxWorkspaces: number | null;
  workspacesRemaining: number | null;
}

function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  return null;
}

function mapQuotaSummary(data: Record<string, unknown>): TenantQuotaUsageSummary {
  const rawPackageId = data.packageId ?? data.PackageId;
  const packageId =
    rawPackageId === null || rawPackageId === undefined ? null : String(rawPackageId).trim() || null;

  return {
    billingPeriodKey: String(data.billingPeriodKey ?? data.BillingPeriodKey ?? ''),
    packageName: (data.packageName ?? data.PackageName ?? null) as string | null,
    packageId,
    eventsUsedThisBillingPeriod: Number(data.eventsUsedThisBillingPeriod ?? data.EventsUsedThisBillingPeriod ?? 0),
    maxEventsPerBillingPeriod: numOrNull(data.maxEventsPerBillingPeriod ?? data.MaxEventsPerBillingPeriod),
    eventsRemaining: numOrNull(data.eventsRemaining ?? data.EventsRemaining),
    usersTracked: Number(data.usersTracked ?? data.UsersTracked ?? 0),
    maxUsers: numOrNull(data.maxUsers ?? data.MaxUsers),
    usersRemaining: numOrNull(data.usersRemaining ?? data.UsersRemaining),
    workspacesUsed: Number(data.workspacesUsed ?? data.WorkspacesUsed ?? 0),
    maxWorkspaces: numOrNull(data.maxWorkspaces ?? data.MaxWorkspaces),
    workspacesRemaining: numOrNull(data.workspacesRemaining ?? data.WorkspacesRemaining),
  };
}

async function fetchQuotaUsage(token: string | undefined): Promise<TenantQuotaUsageSummary> {
  const response = await axios.get<Record<string, unknown>>(`${getBackendBaseUrl()}/settings/quota-usage`, {
    headers: backendRequestHeaders(token),
  });
  return mapQuotaSummary(response.data);
}

export function useQuotaUsage() {
  const { user } = useAuth();
  const token = user?.signInUserSession?.idToken?.jwtToken;

  return useQuery({
    queryKey: [queryKeys.quotaUsage],
    queryFn: () => fetchQuotaUsage(token),
    staleTime: 60_000,
  });
}
